// Riusa il client axios dell'area rappresentante (inietta il representative_token):
// unica fonte per l'auth, niente duplicazione dell'interceptor.
import { repAxios } from './representativeService';
import { printFileFromResponse, printQuery } from './supportPrint';
import type {
  ApproveDraftResult,
  CampiExtra,
  ChannelSettings,
  InvitableResult,
  ManagedChannel,
  Mentionable,
  MentionRef,
  ReplyResult,
  ShareLinksPayload,
  ShareLinksResult,
  TicketPrintFile,
  TicketPrintOptions,
  SupportEmailPrefs,
  SupportEmailPrefsInput,
  SuggestReplyResult,
} from './supportApi';

// ===== Tipi =====
// I tipi del dominio ticket vivono in shared/types/support.ts: qui restano solo
// gli alias storici usati dalle pagine e i tipi specifici delle chiamate REST.

import type {
  ActorType,
  Assignee as SharedAssignee,
  AssigneeRef,
  Candidate,
  GroupLite as SharedGroupLite,
  HistoryEntry,
  InternalNote,
  ListParams,
  ListResult,
  Macro,
  MergeCandidate as SharedMergeCandidate,
  RelatedTicket,
  SavedView as SharedSavedView,
  SavedViewFilters as SharedSavedViewFilters,
  StatsData,
  ThreadMessage,
  TicketDetail,
  TicketListItem,
  TicketSla,
  TicketTransfer,
  TodoData,
  TodoItem,
} from '../../../shared/types/support';

export type GroupLite = SharedGroupLite;
export type Assignee = SharedAssignee;
export type RepAssignee = AssigneeRef;
export type RepCandidate = Candidate;
export type TicketSlaDto = TicketSla;
export type RepTicketListItem = TicketListItem;
export type RepThreadMsg = ThreadMessage;
export type RepInternalNote = InternalNote;
export type RepTransfer = TicketTransfer;
export type RepHistoryEntry = HistoryEntry;
export type RepTicketDetail = TicketDetail;
export type MergeCandidate = SharedMergeCandidate;
export type SupportMacro = Macro;
export type SavedViewFilters = SharedSavedViewFilters;
export type SavedView = SharedSavedView;
export type RepStats = StatsData;
export type SupportTodoItem = TodoItem;
export type SupportTodo = TodoData;
export type ListTicketsResult = ListResult<TicketListItem>;

export type ListTicketsParams = ListParams;

// ===== Funzioni =====

// `assignedCount` (Task 26): ticket attivi assegnati al rappresentante, anche
// fuori dai suoi canali (assegnazione esterna). `invitedCount` (parte B): ticket
// attivi in cui e partecipante invitato.
export const getGroups = async (): Promise<{ mine: GroupLite[]; all: GroupLite[]; assignedCount?: number; invitedCount?: number }> => {
  const res = await repAxios.get('/representative/support/groups');
  return res.data.data;
};

export const listTickets = async (params: ListTicketsParams): Promise<ListTicketsResult> => {
  const res = await repAxios.get('/representative/support/tickets', { params });
  return res.data.data;
};

export const getTicket = async (id: string): Promise<RepTicketDetail> => {
  const res = await repAxios.get(`/representative/support/tickets/${id}`);
  return res.data.data;
};

export const getCandidates = async (id: string): Promise<RepCandidate[]> => {
  const res = await repAxios.get(`/representative/support/tickets/${id}/candidates`);
  return res.data.data;
};

export const take = async (id: string): Promise<void> => {
  await repAxios.post(`/representative/support/tickets/${id}/take`);
};

export const setAssignees = async (
  id: string,
  payload: { add?: Assignee[]; remove?: Assignee[] }
): Promise<void> => {
  await repAxios.post(`/representative/support/tickets/${id}/assignees`, payload);
};

// `pending: true` (Task 46): il rappresentante e componente senza «puo rispondere»,
// la risposta e diventata una bozza da approvare.
export const reply = async (id: string, body: string, files?: File[], thenStatus?: string): Promise<ReplyResult> => {
  let res;
  if (files && files.length > 0) {
    const fd = new FormData();
    fd.append('body', body);
    // Campo testuale del multipart: il backend lo legge da req.body.
    if (thenStatus) fd.append('thenStatus', thenStatus);
    files.forEach((f) => fd.append('files', f));
    // Con FormData NON impostare Content-Type: lo gestisce il browser (boundary).
    res = await repAxios.post(`/representative/support/tickets/${id}/messages`, fd);
  } else {
    res = await repAxios.post(`/representative/support/tickets/${id}/messages`, thenStatus ? { body, thenStatus } : { body });
  }
  const d = (res && res.data) || {};
  return d.pending ? { pending: true, draftId: d.draftId } : { pending: false };
};

// ===== Risposte in bozza (Task 46) =====

export const approveDraft = async (id: string, draftId: string, body?: string): Promise<ApproveDraftResult> => {
  const res = await repAxios.post(`/representative/support/tickets/${id}/drafts/${draftId}/approve`, body !== undefined ? { body } : {});
  return res.data.data;
};

export const rejectDraft = async (id: string, draftId: string, note?: string): Promise<void> => {
  await repAxios.post(`/representative/support/tickets/${id}/drafts/${draftId}/reject`, note ? { note } : {});
};

export const editDraft = async (id: string, draftId: string, body: string): Promise<void> => {
  await repAxios.patch(`/representative/support/tickets/${id}/drafts/${draftId}`, { body });
};

export const deleteDraft = async (id: string, draftId: string): Promise<void> => {
  await repAxios.delete(`/representative/support/tickets/${id}/drafts/${draftId}`);
};

/** Responsabile di canale (`associativa.supporto.manage_channel`): canali e componenti. */
export const getManagedChannels = async (): Promise<ManagedChannel[]> => {
  const res = await repAxios.get('/representative/support/channels/manage');
  return res.data.data;
};

export const setMemberCanReply = async (groupId: string, actorId: string, canReply: boolean): Promise<void> => {
  await repAxios.patch(`/representative/support/channels/${groupId}/members/${actorId}`, { canReply });
};

/** Impostazioni del canale (Task 47): aggiornamento parziale, risposta = impostazioni complete. */
export const setChannelSettings = async (groupId: string, patch: Partial<ChannelSettings>): Promise<ChannelSettings> => {
  const res = await repAxios.patch(`/representative/support/channels/${groupId}/settings`, patch);
  return res.data.data.settings;
};

// `mentions` (Task 48): nel multipart viaggia come stringa JSON; senza menzioni
// la richiesta e identica a prima.
export const addNote = async (id: string, body: string, files?: File[], mentions?: MentionRef[]): Promise<void> => {
  const withMentions = !!(mentions && mentions.length);
  if (files && files.length > 0) {
    const fd = new FormData();
    fd.append('body', body);
    if (withMentions) fd.append('mentions', JSON.stringify(mentions));
    files.forEach((f) => fd.append('files', f));
    await repAxios.post(`/representative/support/tickets/${id}/notes`, fd);
    return;
  }
  await repAxios.post(`/representative/support/tickets/${id}/notes`, withMentions ? { body, mentions } : { body });
};

/** Chi si puo @menzionare nelle note del ticket (Task 48). */
export const getMentionables = async (id: string): Promise<Mentionable[]> => {
  const res = await repAxios.get(`/representative/support/tickets/${id}/mentionables`);
  return res.data.data;
};

/** Popup «Invita» (parte B): pagine da 20, min 2 caratteri. */
export const searchInvitable = async (id: string, q: string, page = 1): Promise<InvitableResult> => {
  const res = await repAxios.get(`/representative/support/tickets/${id}/invitable`, { params: { q, page } });
  return res.data.data;
};

/** Invito manuale dalla card Proprietà (parte B). */
export const addParticipants = async (id: string, add: SharedAssignee[]): Promise<void> => {
  await repAxios.post(`/representative/support/tickets/${id}/participants`, { add });
};

/** Toglie un partecipante dal ticket (parte B). */
export const removeParticipant = async (id: string, actorType: ActorType, actorId: string): Promise<void> => {
  await repAxios.delete(`/representative/support/tickets/${id}/participants/${actorType}/${actorId}`);
};

/**
 * Corregge il testo di un messaggio del thread pubblico. Serve il permesso
 * associativo del lato a cui appartiene il messaggio (richiedente o canale).
 */
export const editMessage = async (id: string, messageId: string, body: string): Promise<void> => {
  await repAxios.patch(`/representative/support/tickets/${id}/messages/${messageId}`, { body });
};

/**
 * Toglie un allegato da un messaggio del thread pubblico (stessi permessi della
 * correzione del testo). `index` = posizione originale dell'allegato.
 */
export const removeAttachment = async (id: string, messageId: string, index: number): Promise<void> => {
  await repAxios.delete(`/representative/support/tickets/${id}/messages/${messageId}/attachments/${index}`);
};

/** Corregge l'oggetto del ticket: basta uno dei due permessi di modifica dei messaggi. */
export const editSubject = async (id: string, subject: string): Promise<void> => {
  await repAxios.patch(`/representative/support/tickets/${id}/subject`, { subject });
};

/** «Dati della pratica» (canale Documento Unico): sostituisce l'insieme dei valori. */
export const setCampiExtra = async (id: string, campiExtra: CampiExtra): Promise<CampiExtra> => {
  const res = await repAxios.put(`/representative/support/tickets/${id}/campi-extra`, { campiExtra });
  return res?.data?.data?.campiExtra || {};
};

/**
 * Note interne (24/10/2026): correzione ed eliminazione. Con il permesso
 * `associativa.supporto.edit_internal_notes` qualsiasi nota, senza la propria
 * entro 15 minuti: decide il backend (403 altrimenti).
 */
export const editNote = async (id: string, noteId: string, body: string): Promise<void> => {
  await repAxios.patch(`/representative/support/tickets/${id}/notes/${noteId}`, { body });
};

export const deleteNote = async (id: string, noteId: string): Promise<void> => {
  await repAxios.delete(`/representative/support/tickets/${id}/notes/${noteId}`);
};

export const getStats = async (): Promise<RepStats> => {
  const res = await repAxios.get('/representative/support/stats');
  return res.data.data;
};

export const setStatus = async (id: string, status: string): Promise<void> => {
  await repAxios.post(`/representative/support/tickets/${id}/status`, { status });
};

/** Cambio del richiedente (permesso `associativa.supporto.change_requester`). */
export const setRequester = async (
  id: string,
  payload: { type: 'guest' | 'associate'; email?: string; nome?: string; associateId?: string }
): Promise<void> => {
  await repAxios.post(`/representative/support/tickets/${id}/requester`, payload);
};

export const closeTicket = async (id: string, motivo?: string): Promise<void> => {
  await repAxios.post(`/representative/support/tickets/${id}/close`, motivo ? { motivo } : {});
};

export const transfer = async (id: string, toGroupId: string, motivo: string, copyGroupIds?: string[]): Promise<void> => {
  // Copie solo se scelte: senza, il body resta quello di prima.
  const body = copyGroupIds && copyGroupIds.length ? { toGroupId, motivo, copyGroupIds } : { toGroupId, motivo };
  await repAxios.post(`/representative/support/tickets/${id}/transfer`, body);
};

/** Mette in copia uno o più canali (parte A). */
export const addCopyGroups = async (id: string, groupIds: string[]): Promise<void> => {
  await repAxios.post(`/representative/support/tickets/${id}/copy-groups`, { groupIds });
};

/** Toglie un canale dalla copia (parte A). */
export const removeCopyGroup = async (id: string, groupId: string): Promise<void> => {
  await repAxios.delete(`/representative/support/tickets/${id}/copy-groups/${groupId}`);
};

/** Il canale in copia diventa responsabile (parte A). */
export const setResponsible = async (id: string, groupId: string, motivo?: string): Promise<void> => {
  await repAxios.post(`/representative/support/tickets/${id}/responsible`, motivo ? { groupId, motivo } : { groupId });
};

// ===== Merge (unione ticket) =====

export const getMergeCandidates = async (id: string): Promise<MergeCandidate[]> => {
  const res = await repAxios.get(`/representative/support/tickets/${id}/merge-candidates`);
  return res.data.data;
};

// Altri ticket dello stesso richiedente (card "Contesto"): include anche i
// risolti/chiusi, a differenza dei candidati al merge.
export const getRelatedTickets = async (id: string): Promise<RelatedTicket[]> => {
  const res = await repAxios.get(`/representative/support/tickets/${id}/related`);
  return res.data.data;
};

export const mergeTicket = async (id: string, targetTicketId: string): Promise<string> => {
  const res = await repAxios.post(`/representative/support/tickets/${id}/merge`, { targetTicketId });
  return res.data.data.targetTicketId;
};

/**
 * Scarica un allegato del ticket (thread pubblico o nota interna) tramite endpoint
 * autenticato rappresentante e lo apre in una nuova scheda. `storedName` e il nome
 * file salvato (att.url).
 */
export const openAttachment = async (ticketId: string, storedName: string, displayName?: string): Promise<void> => {
  // Tab aperta sincronamente (gesto utente) per evitare il blocco popup; poi naviga al blob.
  const win = window.open('', '_blank');
  try {
    const res = await repAxios.get(
      `/representative/support/tickets/${ticketId}/attachments/${encodeURIComponent(storedName)}`,
      { responseType: 'blob' }
    );
    const url = URL.createObjectURL(res.data as Blob);
    if (win) {
      win.location.href = url;
    } else {
      const a = document.createElement('a');
      a.href = url;
      a.download = displayName || storedName;
      a.click();
    }
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (err) {
    if (win) win.close();
    throw err;
  }
};

// ===== Produttività operatori: macro, viste salvate, assist AI =====

export const getMacros = async (groupId?: string): Promise<SupportMacro[]> => {
  const res = await repAxios.get('/representative/support/macros', { params: groupId ? { groupId } : {} });
  return res.data.data;
};

export const getViews = async (): Promise<SavedView[]> => {
  const res = await repAxios.get('/representative/support/views');
  return res.data.data;
};

export const createView = async (nome: string, filtri: SavedViewFilters): Promise<SavedView> => {
  const res = await repAxios.post('/representative/support/views', { nome, filtri });
  return res.data.data;
};

export const deleteView = async (id: string): Promise<void> => {
  await repAxios.delete(`/representative/support/views/${id}`);
};

export const aiRefine = async (text: string, action: string, ticketId?: string): Promise<string> => {
  const res = await repAxios.post('/representative/support/ai/refine', ticketId ? { text, action, ticketId } : { text, action });
  return res.data.data.text;
};

// Task 28: proposta di risposta e riassunto basati sull'intero ticket.
export const suggestReply = async (id: string, draft?: string): Promise<SuggestReplyResult> => {
  const res = await repAxios.post(`/representative/support/tickets/${id}/ai/suggest`, draft ? { draft } : {});
  return { text: res.data.data.text, similarCards: res.data.data.similarCards || [] };
};

// «Condividi via email»: link pubblici firmati agli allegati del ticket.
export const createShareLinks = async (id: string, payload: ShareLinksPayload): Promise<ShareLinksResult> => {
  const res = await repAxios.post(`/representative/support/tickets/${id}/share-links`, payload);
  return res.data.data as ShareLinksResult;
};

// «Stampa»: PDF del ticket generato dal server (stesso perimetro del dettaglio).
export const printTicket = async (id: string, options: TicketPrintOptions): Promise<TicketPrintFile> => {
  const res = await repAxios.get(`/representative/support/tickets/${id}/stampa.pdf`, {
    params: printQuery(options),
    responseType: 'blob',
  });
  return printFileFromResponse(res);
};

// Task 29: «Usa» su una proposta nata con casi simili della base di conoscenza.
export const markSimilarUsed = async (cardIds: string[]): Promise<void> => {
  await repAxios.post('/representative/support/ai/similar-used', { cardIds });
};

export const summarizeTicket = async (id: string): Promise<string> => {
  const res = await repAxios.post(`/representative/support/tickets/${id}/ai/summary`, {});
  return res.data.data.text;
};

export const getSupportTodo = async (): Promise<SupportTodo> => {
  const res = await repAxios.get('/representative/support/todo');
  return res.data.data;
};

// Preferenze email del rappresentante per i ticket (Task 25).
export const getNotificationPreferences = async (): Promise<SupportEmailPrefs> => {
  const res = await repAxios.get('/representative/support/me/notification-preferences');
  return res.data.data;
};

export const saveNotificationPreferences = async (prefs: SupportEmailPrefsInput): Promise<SupportEmailPrefs> => {
  const res = await repAxios.put('/representative/support/me/notification-preferences', prefs);
  return res.data.data;
};

export const sendTestDigest = async (): Promise<{ to: string }> => {
  const res = await repAxios.post('/representative/support/me/notification-preferences/test');
  return res.data.data;
};

// Invalida i link diretti ai ticket gia ricevuti via email (Task 27).
export const revokeSupportLinks = async (): Promise<{ linkVersion: number }> => {
  const res = await repAxios.post('/representative/support/me/notification-preferences/revoke-links');
  return res.data.data;
};

// Popup «Novità del Centro Ticket»: versione scartata dal rappresentante.
export const getNovitaVista = async (): Promise<{ versioneVista: string | null }> => {
  const res = await repAxios.get('/representative/support/me/novita');
  return { versioneVista: res.data?.data?.versioneVista ?? null };
};

export const setNovitaVista = async (versione: string): Promise<void> => {
  await repAxios.put('/representative/support/me/novita', { versione });
};

// Sostituisce le variabili {chiave} (case-insensitive) con i valori forniti.
// Replica lato client di backend/utils/supportMacro.js (variabile sconosciuta → invariata).
export function applyMacroVariables(testo: string, ctx: Record<string, string | undefined>): string {
  if (!testo) return '';
  const lookup: Record<string, string> = {};
  Object.keys(ctx).forEach((k) => { if (ctx[k] != null) lookup[k.toLowerCase()] = String(ctx[k]); });
  return testo.replace(/\{([a-zA-Z_]+)\}/g, (m, key) => {
    const v = lookup[key.toLowerCase()];
    return v != null ? v : m;
  });
}
